/**
 * Static Tab Structure Configuration
 * This defines the permanent, predictable structure of tabs
 * No API calls needed - completely static and declarative
 */

export interface TabDefinition {
  key: string;
  labelKey: string; // Key to look up in language context
  requiresVariant: boolean; // Whether this tab needs variant data
  order: number; // Display order
  enabled: boolean; // Can be used to conditionally disable tabs
  variantKey?: string; // Key to look up in language context
}

export interface VariantTabStructure {
  showVariantTabs: boolean; // Whether to show variant sub-tabs
  defaultVariantId: number; // Fallback variant ID
}

// 🏗️ STATIC TAB STRUCTURE - Never changes, completely predictable
export const ACTIVE_PROJECTS_TABS: TabDefinition[] = [
  {
    key: 'COMPLIANCE_DOCUMENTS',
    labelKey: 'COMPLIANCE_DOCUMENTS',
    requiresVariant: false, // Show variant tabs for consistency, even if content is shared
    order: 1,
    enabled: true,
  },
  {
    key: 'MAIN_DETAILS',
    labelKey: 'MAIN_DETAILS',
    requiresVariant: false, // Do not render variant sub-tabs for Main Details
    order: 2,
    enabled: true,
  },
  {
    key: 'RECIPE_AND_RAW_MATERIALS',
    labelKey: 'RECIPE_AND_RAW_MATERIALS',
    requiresVariant: true,
    order: 3,
    enabled: true,
    variantKey: 'recipeRawMaterialsTab',
  },
  {
    key: 'STORAGE',
    labelKey: 'STORAGE',
    requiresVariant: true,
    order: 4,
    enabled: true,
    variantKey: 'storageTab',
  },
  {
    key: 'NUTRITION',
    labelKey: 'NUTRITION',
    requiresVariant: true,
    order: 5,
    enabled: true,
    variantKey: 'nutritionTab',
  },
  {
    key: 'PACK_COPY',
    labelKey: 'PACK_COPY',
    requiresVariant: false,
    // Render at the end (next to Packaging & Labeling)
    order: 10,
    enabled: true,
  },
  {
    key: 'ARTWORK_FILE',
    labelKey: 'ARTWORK_FILE',
    requiresVariant: false,
    order: 11,
    enabled: true,
  },
  {
    key: 'ALLERGENS_AND_SENSITIVITIES',
    labelKey: 'ALLERGENS_AND_SENSITIVITIES',
    requiresVariant: true,
    order: 6,
    enabled: true,
    variantKey: 'siteAllergen',
  },
  {
    key: 'PACKAGING_AND_LABELLING',
    labelKey: 'PACKAGING_AND_LABELLING',
    requiresVariant: true,
    order: 7,
    enabled: true,
    variantKey: 'otherLabelingTab',
  },
  {
    key: 'PACKAGING_TAB',
    labelKey: 'PACKAGING_TAB',
    requiresVariant: true,
    // Inserted between Storage and Packaging & Labeling in ActiveProject static ordering.
    order: 6.9,
    enabled: true,
    // Use the same variant list source as Packaging & Labeling in multipack flows.
    variantKey: 'packagingTab',
  },
];

// 🎛️ VARIANT STRUCTURE RULES - Simple business logic
export const VARIANT_STRUCTURE_RULES = {
  // When to show variant tabs vs direct tabs
  multiVariantThreshold: 2, // Show variant tabs if >= 2 variants
  defaultSingleVariantId: 1, // Default ID for single variant products
  
  // Tab behavior
  resetVariantTabOnMainTabChange: true,
  preserveVariantSelection: false,
};

// 📋 HELPER FUNCTIONS for tab structure
export const getEnabledTabs = (): TabDefinition[] => 
  ACTIVE_PROJECTS_TABS
    .filter(tab => tab.enabled)
    .sort((a, b) => a.order - b.order);

export const getTabByKey = (key: string): TabDefinition | undefined =>
  ACTIVE_PROJECTS_TABS.find(tab => tab.key === key);

export const shouldShowVariantTabs = (productData: any): boolean => {
  // Use giftPack to determine if variant/section tabs are needed.
  const gpRaw =
    productData?.giftPack ??
    productData?.productMetadata?.giftPack ??
    productData?.multipack?.giftPack;
  const isGiftPack = gpRaw === "true" || gpRaw === true;
  
  // Show variant tabs if it's a gift pack (even if amountOfProductsInGiftPack <= 1).
  return isGiftPack;
};

export const getDefaultVariantId = (variants: any[]): number => {
  if (variants.length === 0) return VARIANT_STRUCTURE_RULES.defaultSingleVariantId;
  return variants[0]?.id || VARIANT_STRUCTURE_RULES.defaultSingleVariantId;
};
